import { JSX, Suspense, useMemo } from "react";

import dayjs from "dayjs";
import { useSummarizedHistoryChartQuery } from "src/api/locationChartQueries.api";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import { ComponentParams } from "src/components/Dashboard/interfaces/DashboardComponentParams";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import TableView from "src/components/TableView/TableView";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import { getMeasuresData } from "src/lib/date.helper";
import { filterByLocationTypeAndSortMeasuresSummarizedDto } from "src/lib/number.helpers";
import useIntervalTabsStore from "src/store/intervalTabs.store";
import useAppPinnedStore from "src/store/pinned.store";
import { type FlowMeasureDto, type MeasuresSummarizedItemDto, type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import { type DateRange } from "src/types/dateRange";

function getRows(
  filteredMeasuresData:
    | {
        data: MeasuresSummarizedItemDto[];
        currentDateRange: DateRange;
        pastDateRange: DateRange;
        flowData: FlowMeasureDto[];
      }
    | undefined
): number[] {
  if (filteredMeasuresData?.data) {
    return [filteredMeasuresData.data.length];
  }
  return [0];
}

function FootfallPinnedLocationsTable({ selectedLocation, displayedType, componentId }: ComponentParams): JSX.Element {
  const { componentStates } = useIntervalTabsStore();
  const { appPinned } = useAppPinnedStore();

  const componentState = componentStates[componentId] ?? {
    selectedTab: IntervalTab.Day,
    selectedDate: undefined,
  };

  const selectedDate = componentState.selectedDate ? dayjs(componentState.selectedDate) : null;

  const filteredAppPinned = useMemo(
    () => appPinned.filter((item) => item.type === displayedType).map((item) => item.id),
    [appPinned, displayedType]
  );

  const isPinned = useMemo(() => filteredAppPinned.length > 0, [filteredAppPinned]);

  const locationIds = useMemo(
    () => (isPinned ? filteredAppPinned : [selectedLocation.Id]),
    [isPinned, filteredAppPinned, selectedLocation.Id]
  );

  const { data: measuresDataToday } = useSummarizedHistoryChartQuery(
    locationIds,
    IntervalType.Day,
    selectedDate,
    MeasureType.Count,
    null,
    null,
    isPinned
  );
  const { data: measuresDataWeek } = useSummarizedHistoryChartQuery(
    locationIds,
    IntervalType.Week,
    selectedDate,
    MeasureType.Count,
    null,
    null,
    isPinned
  );
  const { data: measuresDataMonth } = useSummarizedHistoryChartQuery(
    locationIds,
    IntervalType.Month,
    selectedDate,
    MeasureType.Count,
    null,
    null,
    isPinned
  );
  const { data: measuresDataYear } = useSummarizedHistoryChartQuery(
    locationIds,
    IntervalType.IsoYear,
    selectedDate,
    MeasureType.Count,
    null,
    null,
    isPinned
  );

  const measuresData = useMemo(
    () => getMeasuresData(componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear),
    [componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear]
  ) as MeasuresSummarizedDto;

  const filteredMeasuresData = useMemo(
    () => filterByLocationTypeAndSortMeasuresSummarizedDto(measuresData, displayedType, undefined, 0, 5),
    [measuresData, displayedType]
  );

  const rows = useMemo(() => getRows(filteredMeasuresData), [filteredMeasuresData]);

  return (
    <Suspense fallback={<ChartSkeleton />}>
      {filteredMeasuresData ? (
        <TableView measuresData={filteredMeasuresData} rows={rows} showSeriesColors displayedType={displayedType} hideFooter />
      ) : (
        <ChartSkeleton />
      )}
    </Suspense>
  );
}

export default FootfallPinnedLocationsTable;
